import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import { userActionMessages } from 'src/features/userAction/utils/messages';
import {
    ActiveStayEnded,
    UserActionEvent,
    UserActionType,
    GatewayOpened,
    EmergencyOpenGateway,
    EmergencyCloseGateway,
    MembershipCreated,
    MembershipIdentifierCreated,
    MembershipIdentifierUpdated,
    MembershipIdentifierDeleted,
    MembershipZoneRuleCreated,
    MembershipZoneRuleUpdated,
    MembershipZoneRuleDeleted,
    MembershipAllowedTimeCreated,
    MembershipAllowedTimeUpdated,
    MembershipAllowedTimeDeleted,
    MembershipCloudUserCreated,
    MembershipCloudUserUpdated,
    MembershipCloudUserDeleted,
    MembershipTypeAllowedTimeCreated,
    MembershipTypeAllowedTimeUpdated,
    MembershipTypeActivated,
    MembershipTypeDeactivated,
    MembershipTypeZoneRuleCreated,
    MembershipTypeZoneRuleUpdated,
    MembershipTypeZoneRuleDeleted,
    EntityCreated,
    EntityUpdated,
    EntityDeleted,
    UserCreated,
    UserUpdated,
    UserDeleted,
    UserActivated,
    UserDeactivated,
    MembershipIdentifierArchived,
    VisitorAddedToParking,
} from 'src/features/userAction/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Translation } from 'src/types';

type MessageLinkProps = {
    to: string;
    hasPermission: boolean;
    linkName?: string;
};
const MessageLink = ({ to, linkName, hasPermission }: MessageLinkProps) =>
    to && linkName && hasPermission ? (
        <Link
            to={to}
            className="text-primary-700 hover:text-primary-600 overflow-wrap-normal cursor-pointer font-medium break-keep whitespace-normal"
        >
            {linkName}
        </Link>
    ) : (
        <>{linkName || '-'}</>
    );

const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale)?.name;

type Props = {
    userAction: UserActionEvent;
};

export default function UserActionMessage({ userAction }: Props) {
    const { event_data, action_type } = userAction;
    const accessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);
    const accessMemberships = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const accessMembershipTypes = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    const { formatMessage, locale } = useIntl();

    if (action_type === UserActionType.LOGIN) return formatMessage(userActionMessages.login);
    if (action_type === UserActionType.ACTIVE_STAY_ENDED) {
        const eventData = event_data as ActiveStayEnded;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.activeStayEnded.defaultMessage}
                id={userActionMessages.activeStayEnded.id}
                values={{
                    stay: (
                        <MessageLink
                            hasPermission={accessPastStays}
                            linkName={eventData.active_stay_ref}
                            to={`/${PATH.VISITORS}/past-stay/dashboard/${eventData.past_stay_id}`}
                        />
                    ),
                    identifier: (
                        <MessageLink
                            hasPermission={accessVisitors}
                            linkName={eventData.visitor.main_identifier?.value}
                            to={`/${PATH.VISITORS}/visitor/${eventData.visitor.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.GATEWAY_OPENED) {
        const eventData = event_data as GatewayOpened;
        const name = translate(eventData.gateway.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.gatewayOpened.defaultMessage}
                id={userActionMessages.gatewayOpened.id}
                values={{
                    gateway: (
                        <MessageLink
                            hasPermission={accessGateways}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/gateway/${eventData.gateway.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.EMERGENCY_OPEN_GATEWAY) {
        const eventData = event_data as EmergencyOpenGateway;
        const name = translate(eventData.gateway.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.emergencyGatewayOpened.defaultMessage}
                id={userActionMessages.emergencyGatewayOpened.id}
                values={{
                    gateway: (
                        <MessageLink
                            hasPermission={accessGateways}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/gateway/${eventData.gateway.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.EMERGENCY_CLOSE_GATEWAY) {
        const eventData = event_data as EmergencyCloseGateway;
        const name = translate(eventData.gateway.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.emergencyGatewayClosed.defaultMessage}
                id={userActionMessages.emergencyGatewayClosed.id}
                values={{
                    gateway: (
                        <MessageLink
                            hasPermission={accessGateways}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/gateway/${eventData.gateway.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_CREATED) {
        const eventData = event_data as MembershipCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipCreated.defaultMessage}
                id={userActionMessages.membershipCreated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.name ?? eventData.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_UPDATED) {
        const eventData = event_data as MembershipCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipUpdated.defaultMessage}
                id={userActionMessages.membershipUpdated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.name ?? eventData.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_DELETED) {
        const eventData = event_data as MembershipCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipDeleted.defaultMessage}
                id={userActionMessages.membershipDeleted.id}
                values={{ membership: eventData.name ?? eventData.ref }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_IDENTIFIER_CREATED) {
        const eventData = event_data as MembershipIdentifierCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipIdentifierCreated.defaultMessage}
                id={userActionMessages.membershipIdentifierCreated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    identifier: eventData.identifier.value,
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_IDENTIFIER_UPDATED) {
        const eventData = event_data as MembershipIdentifierUpdated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipIdentifierCreated.defaultMessage}
                id={userActionMessages.membershipIdentifierCreated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    identifier: eventData.identifier.value,
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_IDENTIFIER_DELETED) {
        const eventData = event_data as MembershipIdentifierDeleted;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipIdentifierDeleted.defaultMessage}
                id={userActionMessages.membershipIdentifierDeleted.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    identifier: eventData.identifier.value,
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_IDENTIFIER_ARCHIVED) {
        const eventData = event_data as MembershipIdentifierArchived;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipIdentifierArchived.defaultMessage}
                id={userActionMessages.membershipIdentifierArchived.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    identifier: eventData.identifier.value,
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_ZONE_RULE_CREATED) {
        const eventData = event_data as MembershipZoneRuleCreated;
        const name = translate(eventData.zone.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipZoneRuleCreated.defaultMessage}
                id={userActionMessages.membershipZoneRuleCreated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    zone: (
                        <MessageLink
                            hasPermission={accessZones}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/zone/${eventData.zone.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_ZONE_RULE_UPDATED) {
        const eventData = event_data as MembershipZoneRuleUpdated;
        const name = translate(eventData.zone.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipZoneRuleUpdated.defaultMessage}
                id={userActionMessages.membershipZoneRuleUpdated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    zone: (
                        <MessageLink
                            hasPermission={accessZones}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/zone/${eventData.zone.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_ZONE_RULE_DELETED) {
        const eventData = event_data as MembershipZoneRuleDeleted;
        const name = translate(eventData.zone.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipZoneRuleDeleted.defaultMessage}
                id={userActionMessages.membershipZoneRuleDeleted.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                    zone: (
                        <MessageLink
                            hasPermission={accessZones}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/zone/${eventData.zone.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_ALLOWED_TIME_CREATED) {
        const eventData = event_data as MembershipAllowedTimeCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipAllowedTimeCreated.defaultMessage}
                id={userActionMessages.membershipAllowedTimeCreated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_ALLOWED_TIME_UPDATED) {
        const eventData = event_data as MembershipAllowedTimeUpdated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipAllowedTimeUpdated.defaultMessage}
                id={userActionMessages.membershipAllowedTimeUpdated.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_ALLOWED_TIME_DELETED) {
        const eventData = event_data as MembershipAllowedTimeDeleted;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipAllowedTimeDeleted.defaultMessage}
                id={userActionMessages.membershipAllowedTimeDeleted.id}
                values={{
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_CLOUD_USER_CREATED) {
        const eventData = event_data as MembershipCloudUserCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipCloudUserCreated.defaultMessage}
                id={userActionMessages.membershipCloudUserCreated.id}
                values={{
                    user: eventData.cloud_user.name,
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_CLOUD_USER_UPDATED) {
        const eventData = event_data as MembershipCloudUserUpdated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipCloudUserUpdated.defaultMessage}
                id={userActionMessages.membershipCloudUserUpdated.id}
                values={{
                    user: eventData.cloud_user.name,
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_CLOUD_USER_DELETED) {
        const eventData = event_data as MembershipCloudUserDeleted;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipCloudUserDeleted.defaultMessage}
                id={userActionMessages.membershipCloudUserDeleted.id}
                values={{
                    user: eventData.cloud_user.name,
                    membership: (
                        <MessageLink
                            hasPermission={accessMemberships}
                            linkName={eventData.membership.name ?? eventData.membership.ref}
                            to={`/${PATH.MEMBERSHIPS}/dashboard/${eventData.membership.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ALLOWED_TIME_CREATED) {
        const eventData = event_data as MembershipTypeAllowedTimeCreated;
        const name = translate(eventData.membership_type.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeAllowedTimeCreated.defaultMessage}
                id={userActionMessages.membershipTypeAllowedTimeCreated.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.membership_type.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ALLOWED_TIME_UPDATED) {
        const eventData = event_data as MembershipTypeAllowedTimeUpdated;
        const name = translate(eventData.membership_type.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeAllowedTimeUpdated.defaultMessage}
                id={userActionMessages.membershipTypeAllowedTimeUpdated.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.membership_type.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ALLOWED_TIME_DELETED) {
        const eventData = event_data as MembershipTypeAllowedTimeUpdated;
        const name = translate(eventData.membership_type.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeAllowedTimeDeleted.defaultMessage}
                id={userActionMessages.membershipTypeAllowedTimeDeleted.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.membership_type.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ACTIVATED) {
        const eventData = event_data as MembershipTypeActivated;
        const name = translate(eventData.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeActivated.defaultMessage}
                id={userActionMessages.membershipTypeActivated.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_DEACTIVATED) {
        const eventData = event_data as MembershipTypeDeactivated;
        const name = translate(eventData.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeDeactivated.defaultMessage}
                id={userActionMessages.membershipTypeDeactivated.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={name}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ZONE_RULE_CREATED) {
        const eventData = event_data as MembershipTypeZoneRuleCreated;
        const zoneName = translate(eventData.zone.names, locale);
        const typeName = translate(eventData.membership_type.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeZoneRuleCreated.defaultMessage}
                id={userActionMessages.membershipTypeZoneRuleCreated.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={typeName}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.membership_type.id}`}
                        />
                    ),
                    zone: (
                        <MessageLink
                            hasPermission={accessZones}
                            linkName={zoneName}
                            to={`/${PATH.ADMINISTRATION}/zone/${eventData.zone.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ZONE_RULE_UPDATED) {
        const eventData = event_data as MembershipTypeZoneRuleUpdated;
        const zoneName = translate(eventData.zone.names, locale);
        const typeName = translate(eventData.membership_type.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeZoneRuleUpdated.defaultMessage}
                id={userActionMessages.membershipTypeZoneRuleUpdated.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={typeName}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.membership_type.id}`}
                        />
                    ),
                    zone: (
                        <MessageLink
                            hasPermission={accessZones}
                            linkName={zoneName}
                            to={`/${PATH.ADMINISTRATION}/zone/${eventData.zone.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.MEMBERSHIP_TYPE_ZONE_RULE_DELETED) {
        const eventData = event_data as MembershipTypeZoneRuleDeleted;
        const zoneName = translate(eventData.zone.names, locale);
        const typeName = translate(eventData.membership_type.names, locale);
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.membershipTypeZoneRuleDeleted.defaultMessage}
                id={userActionMessages.membershipTypeZoneRuleDeleted.id}
                values={{
                    membershipType: (
                        <MessageLink
                            hasPermission={accessMembershipTypes}
                            linkName={typeName}
                            to={`/${PATH.ADMINISTRATION}/membership-templates/${eventData.membership_type.id}`}
                        />
                    ),
                    zone: (
                        <MessageLink
                            hasPermission={accessZones}
                            linkName={zoneName}
                            to={`/${PATH.ADMINISTRATION}/zone/${eventData.zone.id}`}
                        />
                    ),
                }}
            />
        );
    }
    if (action_type === UserActionType.ENTITY_CREATED) {
        const eventData = event_data as EntityCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.entityCreated.defaultMessage}
                id={userActionMessages.entityCreated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.ENTITY_UPDATED) {
        const eventData = event_data as EntityUpdated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.entityUpdated.defaultMessage}
                id={userActionMessages.entityCreated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.ENTITY_DELETED) {
        const eventData = event_data as EntityDeleted;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.entityDeleted.defaultMessage}
                id={userActionMessages.entityDeleted.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.USER_CREATED) {
        const eventData = event_data as UserCreated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.userCreated.defaultMessage}
                id={userActionMessages.userCreated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.USER_UPDATED) {
        const eventData = event_data as UserUpdated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.userUpdated.defaultMessage}
                id={userActionMessages.userUpdated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.USER_DELETED) {
        const eventData = event_data as UserDeleted;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.userDeleted.defaultMessage}
                id={userActionMessages.userDeleted.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.USER_ACTIVATED) {
        const eventData = event_data as UserActivated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.userDeleted.defaultMessage}
                id={userActionMessages.userActivated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.USER_DEACTIVATED) {
        const eventData = event_data as UserDeactivated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.userDeleted.defaultMessage}
                id={userActionMessages.userDeactivated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.CLOUD_USER_ACTIVATED) {
        const eventData = event_data as UserDeactivated;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.userDeleted.defaultMessage}
                id={userActionMessages.userDeactivated.id}
                values={{ name: eventData.name }}
            />
        );
    }
    if (action_type === UserActionType.VISITOR_ADDED_TO_PARKING) {
        const eventData = event_data as VisitorAddedToParking;
        return (
            <FormattedMessage
                defaultMessage={userActionMessages.visitorAddedToParking.defaultMessage}
                id={userActionMessages.visitorAddedToParking.id}
                values={{
                    visitor: (
                        <MessageLink
                            hasPermission={accessVisitors}
                            linkName={eventData.visitor.main_identifier?.value}
                            to={`/${PATH.VISITORS}/visitor/${eventData.visitor.id}`}
                        />
                    ),
                }}
            />
        );
    }

    return action_type;
}
